import type { Dispatch, SetStateAction } from 'react';

interface ToolResult {
  toolCallId: string;
  toolName: string;
  data?: unknown;
  error?: string;
  durationMs?: number;
  pending?: boolean;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  streaming?: boolean;
  sources?: {
    id: string;
    content: string;
    metadata?: Record<string, unknown>;
    score?: number;
  }[];
  toolResults?: ToolResult[];
}

interface SSEChunk {
  type: string;
  content?: string;
  sources?: Message['sources'];
  error?: string;
}

/**
 * Handles the "chunk" SSE event type - appends content to current assistant message
 */
export const handleChunkEvent = (
  chunk: SSEChunk,
  setMessages: Dispatch<SetStateAction<Message[]>>,
  currentContent: string,
): string => {
  const newContent = currentContent + (chunk.content ?? '');

  setMessages((prev) => {
    const last = prev[prev.length - 1];
    if (last?.role === 'assistant' && last.streaming === true) {
      return [...prev.slice(0, -1), { ...last, content: newContent }];
    }
    return [
      ...prev,
      {
        role: 'assistant',
        content: newContent,
        streaming: true,
      },
    ];
  });

  return newContent;
};

/**
 * Handles the "end" SSE event type - finalizes the assistant message.
 * Preserves toolResults that streamed in during the turn (Issue #1432).
 *
 * Guards against finalizing the user message when `done` arrives without any
 * preceding assistant chunk (e.g. proactive-RAG-only flow with no LLM output).
 */
export const handleEndEvent = (
  sources: Message['sources'],
  setMessages: Dispatch<SetStateAction<Message[]>>,
  setIsStreaming: (value: boolean) => void,
  eventSource: EventSource,
): void => {
  setMessages((prev) => {
    const last = prev[prev.length - 1];
    if (!last || last.role !== 'assistant') return prev;
    return [
      ...prev.slice(0, -1),
      {
        ...last,
        streaming: false,
        sources,
      },
    ];
  });
  eventSource.close();
  setIsStreaming(false);
};

/**
 * Handles the "error" SSE event type - sets error state and closes stream
 */
export const handleErrorEvent = (
  chunk: SSEChunk,
  setError: Dispatch<SetStateAction<string | null>>,
  setIsStreaming: (value: boolean) => void,
  eventSource: EventSource,
): void => {
  setError(chunk.error ?? 'Stream error');
  eventSource.close();
  setIsStreaming(false);
};

/**
 * Handles `tool_call_start` SSE chunks — appends a pending ToolResult to the
 * current assistant message (creating one if needed). Issue #1432 Phase 2.
 */
export const handleToolCallStartEvent = (
  toolCallId: string,
  toolName: string,
  setMessages: Dispatch<SetStateAction<Message[]>>,
): void => {
  setMessages((prev) => {
    const last = prev[prev.length - 1];
    if (last?.role === 'assistant' && last.streaming === true) {
      return [
        ...prev.slice(0, -1),
        {
          ...last,
          toolResults: [...(last.toolResults ?? []), { toolCallId, toolName, pending: true }],
        },
      ];
    }
    return [
      ...prev,
      {
        role: 'assistant',
        content: '',
        streaming: true,
        toolResults: [{ toolCallId, toolName, pending: true }],
      },
    ];
  });
};

/**
 * Handles `tool_result` SSE chunks — fills in `data`/`error` on the matching
 * pending ToolResult. If no assistant message exists yet (some system-injected
 * tool results arrive without a paired `tool_call_start` and may precede any
 * prose chunk), creates a new assistant message carrying the result.
 * Issue #1432 Phase 2.
 */
export const handleToolResultEvent = (
  toolCallId: string,
  toolName: string,
  data: unknown,
  error: string | undefined,
  durationMs: number | undefined,
  setMessages: Dispatch<SetStateAction<Message[]>>,
): void => {
  setMessages((prev) => {
    const filled: ToolResult = { toolCallId, toolName, data, error, durationMs, pending: false };
    const last = prev[prev.length - 1];
    if (last?.role === 'assistant') {
      const existing = last.toolResults ?? [];
      const idx = existing.findIndex((r) => r.toolCallId === toolCallId);
      const newResults = idx >= 0 ? existing.map((r, i) => (i === idx ? filled : r)) : [...existing, filled];
      return [...prev.slice(0, -1), { ...last, toolResults: newResults }];
    }
    return [
      ...prev,
      {
        role: 'assistant',
        content: '',
        streaming: true,
        toolResults: [filled],
      },
    ];
  });
};
